import { Fragment, type ReactElement } from 'react';

import { Button } from '@apps/web/components/ui/button';
import { Spinner } from '@apps/web/components/ui/spinner';
import {
    type V3ChatAttachment,
    V3ChatAttachmentCard,
} from '@apps/web/features/phoebe_chat_v3';
import { useMode } from '@apps/web/lib/mode_store';
import { useAgentRunFiles } from '@apps/web/swr_data_fetching/data_fetching_hooks';

export function AutomationRunFiles({
    organizationId,
    runId,
    timezone,
}: {
    readonly organizationId: string;
    readonly runId: string;
    readonly timezone: string;
}): ReactElement {
    const mode = useMode();
    const { data, error, isLoading, mutate } = useAgentRunFiles(
        organizationId,
        runId,
    );
    // Workspace-only writes carry no stored bytes; list only delivered files.
    const attachments: Array<V3ChatAttachment> = (data ?? []).flatMap((file) =>
        file.attachment_id === null
            ? []
            : [
                  {
                      kind: 'chat-attachment' as const,
                      version: 1 as const,
                      runId,
                      attachmentId: file.attachment_id,
                      filepath: file.source_filepath,
                      filename: file.name,
                      contentType: file.content_type,
                      disposition: 'attachment' as const,
                      sizeBytes: file.size_bytes ?? 0,
                  },
              ],
    );
    return (
        <div
            data-testid="automation-run-files"
            className="flex flex-wrap items-center gap-2"
        >
            {error === undefined ? null : (
                <Fragment>
                    <p role="alert" className="text-destructive text-sm">
                        Couldn’t load files.
                    </p>
                    <Button
                        type="button"
                        variant="link"
                        size="sm"
                        onClick={() => void mutate()}
                    >
                        Retry
                    </Button>
                </Fragment>
            )}
            {isLoading ? <Spinner size="sm" /> : null}
            {mode === undefined
                ? null
                : attachments.map((attachment) => (
                      <V3ChatAttachmentCard
                          key={attachment.attachmentId}
                          attachment={attachment}
                          context={{
                              organizationId,
                              mode,
                              organizationTimezone: timezone,
                          }}
                      />
                  ))}
        </div>
    );
}
